// src/Api/Api.ts
import axios from "axios";
var apiBaseUri = {
  beta: "https://pixel.xylabs.com",
  local: "http://localhost:3030",
  prod: "https://pixel.xylabs.com"
};
var PixelApi = class {
  endPoint;
  /* baseUri can either be a preset (prod, beta, local), or a specific uri */
  constructor(baseUri = "prod") {
    this.endPoint = apiBaseUri[baseUri] ? `${apiBaseUri[baseUri]}/t/event/queue` : baseUri;
  }
  async trackEvents(events) {
    return (await axios.post(this.endPoint, events)).data;
  }
};

// src/Pixel.ts
import { assertEx } from "@xylabs/assert";
import { Mutex } from "async-mutex";
import Cookies from "js-cookie";
import md5 from "spark-md5";

// src/getSystemInfo.ts
import Bowser from "bowser";
var systemInfo;
var getSystemInfo = () => {
  try {
    systemInfo = systemInfo || Bowser.getParser(globalThis.navigator.userAgent).getResult();
  } catch (ex) {
    console.log(`getSystemInfo Error: ${ex}`);
  }
  return systemInfo;
};

// src/Referrer.ts
var Referrer = class _Referrer {
  static storageId = "_coin_referrer";
  local;
  session;
  constructor() {
    this.session = this.getFromSession() ?? document.referrer;
    sessionStorage.setItem(_Referrer.storageId, this.session);
    this.local = this.getFromLocal() ?? document.referrer;
    globalThis.localStorage.setItem(_Referrer.storageId, this.local);
  }
  toJson() {
    if (this.local && this.local.length > 0 || this.session && this.session.length > 0) {
      return {
        local: this.local,
        session: this.session
      };
    }
  }
  getFromLocal() {
    const value = globalThis.localStorage.getItem(_Referrer.storageId);
    if (value && value.length > 0) {
      return value;
    }
  }
  getFromSession() {
    const value = sessionStorage.getItem(_Referrer.storageId);
    if (value && value.length > 0) {
      return value;
    }
  }
};

// src/UniqueUserId.ts
var UniqueUserId = class _UniqueUserId {
  static localStorageId = "_coin_cid";
  id;
  constructor() {
    this.id = globalThis.localStorage.getItem(_UniqueUserId.localStorageId) ?? this.generateId();
    globalThis.localStorage.setItem(_UniqueUserId.localStorageId, this.id);
  }
  toString() {
    return this.id;
  }
  generateId() {
    return crypto.randomUUID();
  }
};

// src/UtmFields.ts
import isEqual from "fast-deep-equal";
var UtmFields = class _UtmFields {
  static localStorageId = "_coin_utm";
  fields = [];
  constructor() {
    const storedString = globalThis.localStorage.getItem(_UtmFields.localStorageId) ?? "[]";
    try {
      this.fields = JSON.parse(storedString);
    } catch {
      this.fields = [];
    }
    if (!Array.isArray(this.fields)) {
      this.fields = [];
    }
    this.update();
    globalThis.localStorage.setItem(_UtmFields.localStorageId, JSON.stringify(this.fields));
  }
  getUtmRecord = () => {
    const record = {};
    const parsedQueryString = document.location.search.split("?")[1]?.split("&") ?? [];
    for (const item of parsedQueryString) {
      const [fullKey, value] = item.split("=");
      const [keyCategory, keyName] = fullKey.split("_");
      if (keyCategory === "utm") {
        record[keyName] = value;
      }
    }
    return Object.keys(record).length > 0 ? record : null;
  };
  toString() {
    return JSON.stringify(this.fields);
  }
  // check the query string and if there an new/updated utm values, add them to the fields
  update() {
    const record = this.getUtmRecord();
    if (record && !isEqual(this.fields.at(-1), record)) {
      this.fields.push(record);
    }
    return this.fields ?? void 0;
  }
};

// src/Pixel.ts
var emailHashLocalStorageName = "xy_email_hash";
var XyPixel = class _XyPixel {
  static api = new PixelApi();
  static _instance;
  static utmFieldsObj;
  cid = new UniqueUserId().id;
  email;
  email_hash;
  exids;
  pixelId;
  queue = [];
  queueMutex = new Mutex();
  constructor(pixelId) {
    this.pixelId = pixelId;
    this.email_hash = localStorage.getItem(emailHashLocalStorageName);
  }
  static get instance() {
    return assertEx(this._instance, () => "XyPixel uninitialized");
  }
  static init(pixelId) {
    this._instance = new _XyPixel(pixelId);
    return this._instance;
  }
  static selectApi(api) {
    this.api = api;
  }
  static utmFields = () => {
    if (_XyPixel.utmFieldsObj === void 0) {
      _XyPixel.utmFieldsObj = new UtmFields();
    }
    return _XyPixel.utmFieldsObj;
  };
  identify(email) {
    this.email = email;
    this.email_hash = email ? md5.hash(email, true) : void 0;
    if (this.email_hash) {
      localStorage.setItem(emailHashLocalStorageName, this.email_hash);
    }
  }
  async send(event, fields, eventId) {
    this.updateFbId();
    const utm = _XyPixel.utmFields().update();
    const referrer = new Referrer();
    this.queue.push({
      cid: this.cid,
      create_time: Date.now(),
      email_hash: this.email_hash ?? void 0,
      event,
      event_id: eventId,
      exids: this.exids,
      fields,
      host: document.location.host,
      pathname: document.location.pathname,
      pixel: this.pixelId,
      referrer: referrer.toJson(),
      system: getSystemInfo(),
      utm
    });
    await this.tryFlushQueue();
  }
  async tryFlushQueue() {
    await this.queueMutex.runExclusive(async () => {
      if (this.queue.length === 0) return;
      const api = _XyPixel.api;
      if (api) {
        const events = this.queue;
        this.queue = [];
        try {
          await api.trackEvents(events);
        } catch (ex) {
          if (events) {
            this.queue = [...this.queue, ...events];
          }
          console.error(ex);
        }
      }
    });
  }
  updateFbId() {
    this.exids = {
      fbc: Cookies.get("_fbc"),
      fbp: Cookies.get("_fbp"),
      ga: Cookies.get("_ga"),
      gclid: Cookies.get("_gcl_aw"),
      rdt_uid: Cookies.get("rdt_uid"),
      scid: Cookies.get("_scid"),
      tt_sessionId: sessionStorage.getItem("tt_sessionId") ?? void 0
    };
  }
};

// src/UserEventHandler.ts
var UserEventHandler = class {
};

// src/XyUserEventHandler.ts
import { toJsonObject } from "@xylabs/object";
var XyUserEventHandler = class extends UserEventHandler {
  constructor() {
    super();
  }
  async funnelStarted(fields) {
    return await XyPixel.instance.send("FunnelStarted", toJsonObject(fields, [], 10));
  }
  async purchase(fields) {
    return await XyPixel.instance.send("Purchase", toJsonObject(fields, [], 10));
  }
  async testStarted(fields) {
    return await XyPixel.instance.send("TestStarted", toJsonObject(fields, [], 10));
  }
  async userClick(fields) {
    return await XyPixel.instance.send("ViewPage", toJsonObject(fields, [], 10));
  }
  async viewContent(fields) {
    return await XyPixel.instance.send("ViewContent", toJsonObject(fields, [], 10));
  }
};
export {
  PixelApi,
  Referrer,
  UniqueUserId,
  UserEventHandler,
  UtmFields,
  XyPixel,
  XyUserEventHandler
};
//# sourceMappingURL=index.mjs.map